import { useState } from "react";
import { useMeters, useUptime } from "../hooks/useMeters";
import {
  PageHeader,
  MeterSelect,
  Spinner,
  StatCard,
} from "../components/common";
import { fmt } from "../utils/helpers";
import { CheckCircle, WifiOff, Clock, Calendar } from "lucide-react";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function UptimePage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");

  const { data: uptime, isLoading } = useUptime(meterId || meters[0]);
  const pct = uptime?.uptime_pct ?? 0;

  const dialColor = pct > 95 ? "#059669" : pct > 80 ? "#D97706" : "#DC2626";
  const textColor =
    pct > 95
      ? "text-emerald-600"
      : pct > 80
        ? "text-amber-600"
        : "text-red-600";

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Meter Uptime & Connectivity"
          subtitle="Data gap analysis and connectivity health"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
        </PageHeader>

        {isLoading ? (
          <Spinner />
        ) : (
          uptime && (
            <>
              {/* Uptime dial */}
              <div className="ra-card flex flex-col items-center py-10 mb-6">
                <div className="relative w-44 h-44 mb-4">
                  <svg
                    viewBox="0 0 100 100"
                    className="w-full h-full -rotate-90"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke="#E2E8F0"
                      strokeWidth="10"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke={dialColor}
                      strokeWidth="10"
                      strokeDasharray={`${pct * 2.639} 263.9`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${textColor}`}>
                      {pct.toFixed(1)}%
                    </span>
                    <span className="text-xs text-slate-400 mt-0.5">
                      Uptime
                    </span>
                  </div>
                </div>
                <p className="text-slate-500 text-sm">
                  Meter:{" "}
                  <span className="text-slate-800 font-mono font-semibold">
                    {uptime.meter_id}
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                  label="Expected Intervals"
                  value={uptime.total_expected_intervals.toLocaleString()}
                  color="blue"
                />
                <StatCard
                  label="Actual Readings"
                  value={uptime.actual_intervals.toLocaleString()}
                  color="emerald"
                />
                <StatCard
                  label="Longest Gap"
                  value={fmt.num(uptime.longest_gap_hours, 2)}
                  unit="hrs"
                  color={uptime.longest_gap_hours > 2 ? "red" : "amber"}
                />
                <StatCard
                  label="Missing Intervals"
                  value={(
                    uptime.total_expected_intervals - uptime.actual_intervals
                  ).toLocaleString()}
                  color="red"
                />
              </div>

              <div className="ra-card">
                <h2 className="text-sm font-semibold text-slate-700 mb-4">
                  Reporting Period
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
                  <div className="flex items-center gap-3">
                    <Calendar size={16} className="text-blue-500 shrink-0" />
                    <div>
                      <p className="text-slate-400 text-xs">First Reading</p>
                      <p className="text-slate-800 font-mono font-semibold">
                        {fmt.date(uptime.first_reading)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock size={16} className="text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-slate-400 text-xs">Last Reading</p>
                      <p className="text-slate-800 font-mono font-semibold">
                        {fmt.date(uptime.last_reading)}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg border ${
                    pct > 95
                      ? "bg-emerald-50 border-emerald-200"
                      : pct > 80
                        ? "bg-amber-50 border-amber-200"
                        : "bg-red-50 border-red-200"
                  }`}
                >
                  {pct > 95 ? (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle size={16} />
                      <span className="text-sm font-medium">
                        Excellent connectivity — meter is reporting reliably.
                      </span>
                    </div>
                  ) : pct > 80 ? (
                    <div className="flex items-center gap-2 text-amber-700">
                      <WifiOff size={16} />
                      <span className="text-sm font-medium">
                        Some gaps detected — investigate connectivity or power
                        interruptions.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <WifiOff size={16} />
                      <span className="text-sm font-medium">
                        Poor uptime — significant data loss. Meter may need
                        attention.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )
        )}
      </div>
    </Layout>
  );
}
